import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useSessionStore } from '@/stores/session.store';
import { publicRoutes } from './public';
import { gameRoutes } from './game';

const routes: RouteRecordRaw[] = [
  ...publicRoutes,
  ...gameRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true, layout: 'public' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Guard global d'auth : redirige vers /login si la route exige requiresAuth
 * et qu'on n'a pas de token. Si on est deja authentifie et qu'on cherche a
 * aller sur /login ou /register, on renvoie sur la home.
 */
router.beforeEach((to) => {
  const auth = useAuthStore();
  const session = useSessionStore();

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    // Lot 51A : visiteurs non-connectes qui atterrissent sur / sont
    // diriges vers la landing publique (marketing) plutot que sur
    // /login. Les autres routes protegees continuent a passer par
    // /login avec ?redirect pour preserver la cible originale.
    if (to.path === '/') {
      return { name: 'landing' };
    }
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  if ((to.name === 'login' || to.name === 'register') && auth.isAuthenticated) {
    return { name: 'home' };
  }

  // Back-office : réservé aux porteurs d'un rôle admin (Administrateur /
  // WebMaster). Un joueur normal qui force une URL /admin/* est renvoyé sur
  // la home plutôt que d'entrer dans l'UI d'admin (le backend 403 de toute
  // façon ; ici on évite d'exposer l'interface). Toutes les routes admin
  // portent meta.layout='admin'.
  if (to.meta.layout === 'admin' && !auth.isAdmin) {
    return { name: 'home' };
  }

  // Lobby univers : interdit l'accès tant que le joueur a un univers
  // actif. Pour revenir au lobby, il doit explicitement cliquer
  // "Quitter l'univers" dans l'AppHeader (qui appelle session.clear()).
  if (to.name === 'lobby' && session.inUniverse) {
    return { name: 'home' };
  }

  // Sans univers actif, seules les routes hors-game sont permises :
  // /lobby (choix d'univers), /admin (back-office), et la home qui
  // se chargera de rediriger vers le lobby. Les routes de jeu
  // (planet, galaxy, fleets, research, etc.) sont donc bloquées tant
  // que le joueur n'a pas rejoint un univers.
  if (
    to.meta.requiresAuth
    && auth.isAuthenticated
    && !session.inUniverse
    && !to.path.startsWith('/admin')
    && to.name !== 'lobby'
    && to.name !== 'home'
  ) {
    return { name: 'lobby' };
  }

  return true;
});

export default router;
